import { useState, useEffect } from 'react';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { IProductReview } from '@monofront/types';
import { GET_PRODUCT_REVIEWS } from './../../queries/productReview';
import { SORT_PRODUCT_REVIEWS_BY_ACTION } from './../../utils';

const ITEMS_PER_PAGE_COUNT_DEFAULT = 10;

export interface IUseProductReviewReturn {
  error: ApolloError | undefined;
  productReviews: IProductReview[];
  isLoading: boolean;
  getProductReviews: (sku: string) => void;
  seeMoreProductReviews: () => void;
  canSeeMoreProduct: boolean;
  sortProductReviews: (by: string) => void;
  displayedReviewsCount: number;
  totalReviewsCount: number;
  itemsPerPageCount: number;
}

export const useGetProductReviews = (
  itemsPerPageCount = ITEMS_PER_PAGE_COUNT_DEFAULT,
): IUseProductReviewReturn => {
  const [productSku, setProductSku] = useState<string | null>(null);
  const [productReviews, setProductReviews] = useState<IProductReview[]>([]);
  const [displayedItemsCount, setDisplayedItemsCount] =
    useState(itemsPerPageCount);
  const [getProductReviewsQuery, { called, loading, data, error }] =
    useLazyQuery(GET_PRODUCT_REVIEWS, {
      ssr: false,
      fetchPolicy: 'cache-and-network',
    });

  useEffect(() => {
    if (productSku != null) {
      getProductReviewsQuery({
        variables: { sku: productSku, page: 1, itemsPerPage: 300 },
      });
    }
  }, [productSku]);

  useEffect(() => {
    if (called && !loading) {
      const reviews = data?.getProductReviews?.length
        ? data?.getProductReviews
        : [];

      setProductReviews(reviews);
      setDisplayedItemsCount(
        reviews.length < itemsPerPageCount ? reviews.length : itemsPerPageCount,
      );
    }
  }, [data?.getProductReviews, loading]);

  const getProductReviews = (sku: string): void => {
    setProductSku(sku);
  };

  const seeMoreProductReviews = () =>
    setDisplayedItemsCount((prev) =>
      prev + itemsPerPageCount > productReviews.length
        ? productReviews.length
        : prev + itemsPerPageCount,
    );

  const sortProductReviews = (by: string): void => {
    if (SORT_PRODUCT_REVIEWS_BY_ACTION[by]) {
      setProductReviews((prev) => [
        ...SORT_PRODUCT_REVIEWS_BY_ACTION[by](prev),
      ]);
    }
  };

  return {
    error,
    isLoading: loading,
    productReviews: productReviews.slice(0, displayedItemsCount),
    getProductReviews,
    seeMoreProductReviews,
    canSeeMoreProduct: displayedItemsCount < productReviews.length,
    sortProductReviews,
    displayedReviewsCount: displayedItemsCount,
    totalReviewsCount: productReviews.length,
    itemsPerPageCount,
  };
};
